import { format } from 'date-fns/format';
import React, { useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDatePicker from '../../components/GenericDatePicker';
import GenericDropdown from '../../components/GenericDropDown';
import GenericInput from '../../components/GenericInput';
import GenericModal from '../../components/GenericModal';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { BetViewRequest } from '../../models/BetViewRequest';
import { GameConfig } from '../../models/GameConfig';
import { Result } from '../../models/Result';
import { getAllActiveGameConfigs, getManagerGamePermissions } from '../../redux/entities/gameEntity';
import { publishResult } from '../../redux/entities/resultEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useAuth } from '../../context/AuthContext';
import { Role } from '../../utils/rolesUtil';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const PublishResultPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [request, setRequest] = useState<BetViewRequest>({} as BetViewRequest);
  const [resultTimeOptions, setResultTimeOptions] = useState<any[]>([]);
  const [selectedResultTime, setSelectedResultTime] = useState<string>('');
  const [result, setResult] = useState<Result>({} as Result);
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();
  const { user, role } = useAuth();
  const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const managerGamePermissions = useAppSelector((state) => state.entities.gameReducer.managerPermissions);

  useEffect(() => {
    if (managerGamePermissions.length === 0 && role === Role.Manager) {
      dispatch(getManagerGamePermissions({ managerId: user?.id.toString() }))
        .unwrap()
        .catch((err: Error) => {
          showModalStrip('danger', err.message, 5000);
        });
    }
  }, [dispatch, managerGamePermissions.length, role, user?.id, showModalStrip]);

  useEffect(() => {
    const fetchGameConfigs = async () => {
      if ((!gameConfigs || gameConfigs.length === 0) && !isLoading && !hasFetched) {
        try {
          setIsLoading(true);
          await dispatch(getAllActiveGameConfigs()).unwrap();
          setHasFetched(true);
        } catch (err) {
          showModalStrip('danger', err as string, 5000);
          setShowConfirmModal(false);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchGameConfigs();
  }, [dispatch, gameConfigs, isLoading, hasFetched, showModalStrip]);

  // Group gameConfigs by game name
  const groupedGameConfigs = gameConfigs.reduce((acc, config) => {
    const gameName = config?.game?.name;
    if (!acc[gameName]) {
      acc[gameName] = [];
    }
    acc[gameName].push(config);
    return acc;
  }, {} as Record<string, GameConfig[]>);

  // Get the resultTimes based on selected game
  const getResultTimes = useCallback((gameName: string) => {
    const selectedGameConfig = groupedGameConfigs[gameName] || [];
    return selectedGameConfig.map((config: any) => config.resultTime);
  }, [groupedGameConfigs]);


  useEffect(() => {
    if (selectedGame) {
      const resultTimes = getResultTimes(selectedGame).map(resultTime => ({
        value: resultTime.id.toString(),
        label: resultTime.resultTime,
      }));

      // Sorting the times based on the label (which is in 12-hour format like "10:30 AM")
      const sortedResultTimes = resultTimes.sort((a, b) => {
        const timeA = new Date(`01/01/2020 ${a.label}`); // Adding a fixed date to parse the time correctly
        const timeB = new Date(`01/01/2020 ${b.label}`);
        return timeA.getTime() - timeB.getTime();
      });

      setResultTimeOptions(sortedResultTimes);
    }
  }, [selectedGame, getResultTimes]);

  // Handle form submission (save bet)
  const handlePublishResult = () => {
    // Validation
    if (!selectedGame || !selectedResultTime) {
      alert('All fields are required');
      return;
    }

    setShowConfirmModal(true);

  };

  const handleConfirmPublish = () => {
    // Dispatch action to save the bet
    dispatch(publishResult(result)).unwrap()
      .then((res: any) => {
        setShowConfirmModal(false);
        showModalStrip('success', 'Result Published successfully', 5000);
      })
      .catch((err: any) => {
        setShowConfirmModal(false);
        showModalStrip('danger', err.message, 5000);
      });
  }

  const modalContent = (
    <div>
      <p><strong>Game:</strong> {result.gameDTO?.name}</p>
      <p><strong>Result Time:</strong> {result.resultTime?.resultTime}</p>
      {
        result.singleResult && result.pattiResult &&
        <>
          <p><strong>Single Result:</strong> {result.singleResult}</p>
          <p><strong>Patti Result:</strong> {result.pattiResult}</p>
        </>
      }
      {
        result.burfiResult &&
        <p><strong>Barfi Result:</strong> {result.burfiResult}</p>
      }
      {result.digitResult &&
        <p><strong>Digit Result:</strong> {result.digitResult}</p>
      }
      <p>Are you sure you want to publish this result?</p>
      <GenericButton
        label="Confirm"
        onClick={() => handleConfirmPublish()}
      />
      <GenericButton
        label="Cancel"
        type="secondary"
        onClick={() => setShowConfirmModal(false)}
      />
    </div>
  );

  return (
    <Container>
      <PageHeading>Publish Result</PageHeading>
      <TableWrapper>

        <GenericDatePicker
          label='Select Date'
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setResult({ ...result, resultDate: format(date == null ? new Date() : date, "yyyy-MM-dd") })
            setRequest({ ...request, date: format(date == null ? new Date() : date, "yyyy-MM-dd") });
          }

          }

        />

        <GenericDropdown
          label="Select Game"
          options={role === Role.Manager
            ? managerGamePermissions
              .filter(permission => permission.status === 'ACTIVE')
              .map(permission => ({
                value: permission.gameDTO.name,
                label: permission.gameDTO.name
              }))
            : Object.keys(groupedGameConfigs).map((gameName) => ({
              value: gameName,
              label: gameName,
            }))
          }
          onChange={(gameName) => {
            setSelectedGame(gameName);
            setResult({ ...result, gameDTO: gameConfigs.find(x => x?.game?.name === gameName)?.game })
            setRequest({ ...request, gameId: groupedGameConfigs[gameName] && gameName?.id !== '0' ? groupedGameConfigs[gameName][0]?.game?.id : undefined });
          }}
          value={selectedGame}
        />

        {/* Select Result Time */}
        <GenericDropdown
          label="Select Result Time"
          options={resultTimeOptions}
          onChange={(resultTime) => {
            setSelectedResultTime(resultTime);
            setResult({ ...result, resultTime: gameConfigs.find(x => x.resultTime.id.toString() === resultTime)?.resultTime })
            setRequest({ ...request, resultTimeId: resultTime !== '' && resultTime !== '0' ? resultTime : undefined });
          }}
          value={selectedResultTime}
        />

        {!gameConfigs.filter(x => x?.game?.name === selectedGame)[0]?.game?.digitSupported && !gameConfigs.filter(x => x?.game?.name === selectedGame)[0]?.game?.burfiSupported &&
          <>
            <GenericInput
              label="Single Result"
              value={result.singleResult}
              onChange={(e) => setResult({ ...result, singleResult: e })}
            />

            <GenericInput
              label="Patti Result"
              value={result.pattiResult}
              onChange={(e) => setResult({ ...result, pattiResult: e })}
            />
          </>

        }

        {gameConfigs.filter(x => x?.game?.name === selectedGame)[0]?.game?.digitSupported &&
          <GenericInput
            label="Digit Result"
            value={result.digitResult}
            onChange={(e) => setResult({ ...result, digitResult: e })}
          />
        }

        {gameConfigs.filter(x => x?.game?.name === selectedGame)[0]?.game?.burfiSupported &&
          <GenericInput
            label="Barfi Result"
            value={result.burfiResult}
            onChange={(e) => setResult({ ...result, burfiResult: e })}
          />
        }

        <GenericButton
          label="Publish"
          onClick={() => handlePublishResult()}
        />

        {showConfirmModal &&
          <GenericModal
            isOpen={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            node={modalContent}
            label="Confirm Publish Result"
          />
        }


      </TableWrapper>

      {showConfirmModal &&
        <GenericModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          node={modalContent}
          label="Confirm Publish Result"
        />
      }

    </Container>
  );
};

export default PublishResultPage;
